//# sourceMappingURL=list_.mjs.map
/// <reference types="./list_.d.mts" />
import * as $bool from "../../gleam_stdlib/gleam/bool.mjs";
import * as $dict from "../../gleam_stdlib/gleam/dict.mjs";
import * as $list from "../../gleam_stdlib/gleam/list.mjs";
import * as $pair from "../../gleam_stdlib/gleam/pair.mjs";
import { Ok, Error, toList, Empty as $Empty, isEqual } from "../gleam.mjs";

/**
 * Append an element in queue of the list. Runs in O(n). Be careful when using
 * it, and almost always prefer to use another solution if possible. Pushing to
 * a queue is a sign that the List data structure could be a wrong choice.
 *
 * ```gleam
 * extra.push([1, 2, 3, 4, 5], 6)
 * // -> [1, 2, 3, 4, 5, 6]
 * ```
 */
export function push(list, element) {
  return $list.append(list, toList([element]));
}

/**
 * Appends an element at the end of the list, iif that element is not in
 * the list.
 *
 * ```gleam
 * extra.insert([1, 2, 3], 4)
 * // -> [1, 2, 3, 4]
 * ```
 *
 * ```gleam
 * extra.insert([1, 2, 3], 2)
 * // -> [1, 2, 3]
 * ```
 */
export function insert(list, element) {
  let present = $list.contains(list, element);
  return $bool.guard(present, list, () => { return push(list, element); });
}

/**
 * Remove every occurences of `element` from the list.
 *
 * ```gleam
 * extra.remove([1, 2, 1, 3], 1)
 * // -> [2, 3]
 * ```
 * ```gleam
 * extra.remove([1, 2, 1, 3], 4)
 * // -> [1, 2, 1, 3]
 * ```
 */
export function remove(list, element) {
  return $list.filter(list, (e) => { return !isEqual(e, element); });
}

/**
 * Return a sublist of the given list.
 * ```gleam
 * [1, 2, 3, 4, 5, 6]
 *  |> list.slice(2, 5)
 * // [3, 4, 5]
 * ```
 */
export function slice(l, start, end) {
  let _pipe = $list.drop(l, start);
  return $list.take(_pipe, end - start);
}

/**
 * Find n-th element in a List. Using `at` is usually the sign of a wrongly
 * architectured application. However, it can be sometimes useful, under some
 * specific circumstances.
 */
export function at(loop$l, loop$index) {
  while (true) {
    let l = loop$l;
    let index = loop$index;
    if (l instanceof $Empty) {
      return new Error(undefined);
    } else if (index === 0) {
      let fst = l.head;
      return new Ok(fst);
    } else {
      let n = index;
      let rest = l.tail;
      loop$l = rest;
      loop$index = n - 1;
    }
  }
}

/**
 * Finds the n-th element in a list, starting from the `start` position
 * find_nth([1, 2, 3, 4], 2, 0) -> Ok(3)
 */
export function nth(loop$l, loop$index, loop$start) {
  while (true) {
    let l = loop$l;
    let index = loop$index;
    let start = loop$start;
    if (l instanceof $Empty) {
      return new Error(undefined);
    } else if (index === start) {
      let first = l.head;
      return new Ok(first);
    } else {
      let rest = l.tail;
      loop$l = rest;
      loop$index = index;
      loop$start = start + 1;
    }
  }
}

export function index_filter(l, f) {
  let _pipe = l;
  let _pipe$1 = $list.index_map(_pipe, (a, i) => { return [a, i]; });
  let _pipe$2 = $list.filter(_pipe$1, (t) => { return f(t[1]); });
  return $list.map(_pipe$2, $pair.first);
}

/**
 * upsert the new elements in the old list
 * replace only append if `id(a) == id(b)`
 */
export function upsert_by_id(old, new$, id) {
  let _block;
  let _pipe = old;
  let _pipe$1 = $list.map(_pipe, (o) => { return [id(o), o]; });
  _block = $dict.from_list(_pipe$1);
  let old$1 = _block;
  let _block$1;
  let _pipe$2 = new$;
  let _pipe$3 = $list.map(_pipe$2, (n) => { return [id(n), n]; });
  _block$1 = $dict.from_list(_pipe$3);
  let new$1 = _block$1;
  let _pipe$4 = $dict.combine(old$1, new$1, (_, new$) => { return new$; });
  return $dict.values(_pipe$4);
}

function index_of_rec(loop$l, loop$element, loop$index) {
  while (true) {
    let l = loop$l;
    let element = loop$element;
    let index = loop$index;
    if (l instanceof $Empty) {
      return new Error(undefined);
    } else {
      let hd = l.head;
      if (isEqual(hd, element)) {
        return new Ok(index);
      } else {
        let index$1 = index;
        let tl = l.tail;
        loop$l = tl;
        loop$element = element;
        loop$index = index$1 + 1;
      }
    }
  }
}

/**
 * Finds the index of the given element in the given list. Runs in O(n).
 *
 * ```gleam
 * list_.index_of(["hello", "world", "good", "morning"], "world") // -> Ok(1)
 * list_.index_of(["hello", "world", "good", "morning"], "hi") // -> Error(Nil)
 * ```
 */
export function index_of(l, element) {
  return index_of_rec(l, element, 0);
}

export function join(ls) {
  return $list.append(ls[0], ls[1]);
}

/**
 * Compares an old and a new list, returning `#(removed, added)`: elements
 * present in `old` but not `new`, and elements present in `new`. Useful to
 * reconcile a stored set of ids against a freshly computed one (delete
 * `removed`, upsert `added`).
 *
 * ```gleam
 * list_.select_remove_add([1, 2, 3], [2, 3, 4])
 * // -> #([1], [2, 3, 4])
 * ```
 */
export function select_remove_add(old, new$) {
  let removed = $list.filter(old, (e) => { return !$list.contains(new$, e); });
  let added = $list.filter(new$, (e) => { return !$list.contains(removed, e); });
  return [removed, added];
}

function do_length_while(l, below, iteration) {
  return $bool.guard(
    below === iteration,
    new Error(undefined),
    () => {
      if (l instanceof $Empty) {
        return new Ok(iteration);
      } else {
        let rest = l.tail;
        return do_length_while(rest, below, iteration + 1);
      }
    },
  );
}

export function length_while(l, below) {
  return do_length_while(l, below, 0);
}
